// Logique pure de la timeline de l'agent (PER-2433) : libellés, couleurs,
// boutons à afficher, et depuis PER-2500 le découpage par compagnie. Aucun
// import React : testable avec jest tel quel.

export const ETATS_AGENT = {
  // Verdict rendu sans job (PER-2449) : la demande peut être lancée, ou l'agent ne la traitera pas.
  eligible: { label: "Éligible, à lancer", color: "#0e7490", icon: "fa-check-square-o" },
  non_eligible: { label: "Non éligible", color: "#57534e", icon: "fa-ban" },
  en_file: { label: "En file d'attente", color: "#6c757d", icon: "fa-clock-o" },
  en_cours: { label: "En cours", color: "#0d6efd", icon: "fa-cog fa-spin" },
  attente_action: {
    label: "En attente d'une action",
    color: "#d97706",
    icon: "fa-hand-paper-o",
  },
  termine: { label: "Terminé", color: "#16a34a", icon: "fa-check-circle" },
  echec: { label: "Échec", color: "#dc2626", icon: "fa-times-circle" },
};

export const NIVEAUX = {
  ok: { color: "#16a34a" },
  attention: { color: "#d97706" },
  echec: { color: "#dc2626" },
};

export const ICONES_TYPES = {
  eligibilite: "fa-check-square-o",
  mise_en_file: "fa-inbox",
  demarrage: "fa-play",
  etape: "fa-arrow-right",
  jalon: "fa-flag",
  tarifs: "fa-eur",
  attente_action: "fa-hand-paper-o",
  depot: "fa-file-pdf-o",
  fin: "fa-check",
  echec: "fa-times",
  avertissement: "fa-exclamation-triangle",
  info: "fa-info-circle",
};

// Compagnie visée par les routes de graal qui ne la précisent pas (la première
// de son référentiel) : sert de repli tant que graal ne rend pas `compagnies`.
export const COMPAGNIE_PAR_DEFAUT = "assurali";

// Noms de repli, pour un graal d'avant PER-2498 (sans `compagnies[]`) ou pour
// une compagnie citée par un événement sans être dans le référentiel rendu.
// Le référentiel de graal fait foi dès qu'il est là (`nomCompagnie`).
const NOMS_COMPAGNIES_REPLI = { assurali: "AssurAli", prima: "Allianz (Prima)", april: "April" };

/**
 * Nom lisible d'une compagnie à partir de sa clé (`assurali` → AssurAli) :
 * celui du référentiel de graal quand `compagnies[]` (timeline) est fourni,
 * sinon le repli ci-dessus, sinon la clé capitalisée.
 */
export const nomCompagnie = (compagnie, compagnies) => {
  const connue = Array.isArray(compagnies) ? compagnies.find((c) => c && c.compagnie === compagnie) : null;
  if (connue?.nom) return connue.nom;
  return (
    NOMS_COMPAGNIES_REPLI[compagnie] ||
    (compagnie ? compagnie.charAt(0).toUpperCase() + compagnie.slice(1) : "la compagnie")
  );
};

export const etatAgentInfo = (etat) =>
  (etat && ETATS_AGENT[etat]) || {
    label: "Aucune intervention",
    color: "#adb5bd",
    icon: "fa-minus-circle",
  };

const travaille = (t) =>
  Boolean(
    t &&
      (["en_file", "en_cours"].includes(t.etat) ||
        (t.commandeEnCours && ["en_attente", "transmise"].includes(t.commandeEnCours.statut))),
  );

/**
 * L'agent est actif : la timeline se rafraîchit toute seule. Vrai dès qu'une
 * compagnie travaille ou attend une commande, le résumé de tête ne portant
 * que l'état le plus urgent (PER-2500).
 */
export const agentActif = (timeline) =>
  travaille(timeline) || (Array.isArray(timeline?.compagnies) && timeline.compagnies.some(travaille));

export const BOUTONS = {
  // Demande écartée par le verdict (PER-2449) : pas de lancement, mais une
  // nouvelle vérification après correction de la demande.
  reevaluer: {
    type: "reevaluer",
    label: "Vérifier de nouveau l'éligibilité",
    icon: "fa-refresh",
    className: "btn-default",
    confirmation: null,
  },
  lancer: {
    type: "lancer",
    label: "Lancer l'agent",
    icon: "fa-play",
    className: "btn-primary",
    confirmation:
      "Lancer l'agent de tarification sur cette demande ? Il ira chercher les tarifs sur l'extranet de la compagnie et s'arrêtera avant toute action irréversible tant que les interrupteurs sont fermés.",
  },
  creation_projet: {
    type: "franchir",
    interrupteur: "creation_projet",
    label: "Créer le projet et poursuivre",
    icon: "fa-folder-open",
    // Classe propre : le « warning » du thème (#ffcc99) est illisible avec du texte blanc.
    className: "btn-agent-warning",
    confirmation:
      "Créer le projet et le client chez la compagnie ? Cette action est irréversible (un projet et un client sont créés chez la compagnie). L'agent produira ensuite le devis et s'arrêtera avant de l'envoyer au prospect si cet interrupteur est fermé.",
  },
  envoi_prospect: {
    type: "franchir",
    interrupteur: "envoi_prospect",
    label: "Envoyer le devis au prospect",
    icon: "fa-paper-plane",
    className: "btn-danger",
    confirmation:
      "Envoyer le devis au prospect ? L'email part de la compagnie, avec le devis et ses annexes : cette action est irréversible.",
  },
  // Régénération du devis après modification de la demande (PER-2470) : le
  // projet existant chez la compagnie est repris, un nouveau devis est produit.
  regenerer: {
    type: "regenerer",
    label: "Régénérer le devis",
    icon: "fa-repeat",
    className: "btn-agent-warning",
    confirmation:
      "Régénérer le devis chez la compagnie ? Le projet existant est repris avec les valeurs actuelles de la demande : la compagnie crée une nouvelle version du projet et un nouveau devis. L'envoi au prospect suit ensuite l'interrupteur « Envoi du devis au prospect » : fermé, l'agent s'arrête avant l'enveloppe ; ouvert, la compagnie envoie le nouveau devis.",
  },
};

// Interrupteurs connus tant que graal n'en rend aucun (timeline partielle).
const INTERRUPTEURS_REPLI = ["creation_projet", "envoi_prospect"];

/** Un interrupteur devant lequel l'agent s'arrête et qu'un bouton de la timeline franchit. */
const estFranchissable = (nom) => Boolean(nom && BOUTONS[nom] && BOUTONS[nom].type === "franchir");

/**
 * Interrupteurs franchissables d'une compagnie, dérivés de ses interrupteurs
 * tels que graal les rend (PER-2500) : seuls ceux qui ont un bouton ici
 * comptent, `etatDetail` portant aussi d'autres noms (ex. « devis_envoye »)
 * et graal d'autres interrupteurs (« declenchement_auto »). Sans interrupteurs
 * rendus, les deux connus.
 */
export const interrupteursFranchissables = (timeline) => {
  const noms = Object.keys(timeline?.interrupteurs || {});
  return noms.length ? noms.filter(estFranchissable) : INTERRUPTEURS_REPLI;
};

/** Un devis a déjà été généré chez la compagnie sur cette demande (jalon de l'agent, quel que soit le job). */
export const aUnDevisGenere = (timeline) =>
  (timeline?.evenements || []).some((e) => e.type === "jalon" && e.detail?.nom === "devis_genere");

/** Libellé lisible d'une commande du back-office (alertes « commande en cours » et « commande refusée »). */
export const libelleCommande = (commande) => {
  if (!commande) return "";
  if (commande.type === "lancer") return BOUTONS.lancer.label;
  if (commande.type === "regenerer") return BOUTONS.regenerer.label;
  if (commande.type === "franchir" && estFranchissable(commande.interrupteur)) {
    return BOUTONS[commande.interrupteur].label;
  }
  // Commande que ce back-office ne connaît pas (graal plus récent) : son type
  // brut vaut mieux qu'un libellé faux.
  return commande.type || "commande inconnue";
};

/**
 * Arrêt sur un préalable non rempli (PER-2458) plutôt que sur un interrupteur
 * fermé : l'agent s'est arrêté devant une action humaine à faire d'abord (ex.
 * compléter la fiche client chez la compagnie) et l'a dit dans son dernier
 * événement « attente_action » (`detail.motif`, explication). Rend ce motif,
 * ou null quand l'attente tient seulement à l'interrupteur.
 * Seuls comptent les événements de l'interrupteur attendu (`etatDetail`) :
 * une attente plus récente sur un autre interrupteur ne dit rien de l'arrêt
 * courant. Sans `etatDetail` (timeline partielle), le dernier événement fait foi.
 */
export const motifAttente = (timeline) => {
  if (!timeline || timeline.etat !== "attente_action") return null;
  const interrupteur = timeline.etatDetail;
  const attentes = (timeline.evenements || []).filter(
    (e) => e.type === "attente_action" && (!interrupteur || e.detail?.interrupteur === interrupteur),
  );
  const derniere = attentes[attentes.length - 1];
  if (!derniere || !derniere.detail?.motif) return null;
  return { motif: derniere.detail.motif, explication: derniere.explication || "" };
};

/**
 * Boutons disponibles selon la timeline (d'une compagnie, PER-2500) et la demande (PER-2433, PER-2434, PER-2449) :
 *  - « Lancer l'agent » : demande finalisée, éligible ou sans verdict, aucun job ouvert ni commande ouverte ;
 *  - « Vérifier de nouveau l'éligibilité » : demande écartée par le verdict de l'agent (jamais « Lancer ») ;
 *  - « Créer le projet et poursuivre » / « Envoyer le devis au prospect » :
 *    l'agent attend exactement cet interrupteur ; aucune commande ouverte.
 * L'agent arrêté devant un interrupteur ne repart jamais seul, même si cet
 * interrupteur est ouvert ensuite dans les paramètres : le bouton reste proposé
 * et c'est lui qui relance l'agent (PER-2469, décision de Perceval du 06/10 ;
 * couvre aussi l'arrêt sur un préalable, PER-2458).
 *  - « Régénérer le devis » (PER-2470) : dès qu'un devis a été généré chez la
 *    compagnie et que l'agent est inactif, en plus du bouton de l'état courant.
 */
export const boutonsDisponibles = (timeline, lead) => {
  if (!timeline || !lead?.isFinalized) return [];
  if (timeline.commandeEnCours && ["en_attente", "transmise"].includes(timeline.commandeEnCours.statut)) {
    return [];
  }
  const etat = timeline.etat;
  if (etat === "non_eligible") return [BOUTONS.reevaluer];
  if (etat === "en_file" || etat === "en_cours") return [];
  const boutons = [];
  if (!etat || etat === "eligible" || etat === "termine" || etat === "echec") boutons.push(BOUTONS.lancer);
  if (etat === "attente_action" && interrupteursFranchissables(timeline).includes(timeline.etatDetail)) {
    boutons.push(BOUTONS[timeline.etatDetail]);
  }
  if (aUnDevisGenere(timeline)) boutons.push(BOUTONS.regenerer);
  return boutons;
};

/** La compagnie a été sollicitée sur cette demande : un état, un événement, une commande ou un verdict. */
const estSollicitee = (compagnie, evenements) =>
  Boolean(
    compagnie.etat ||
      evenements.length ||
      compagnie.commandeEnCours ||
      compagnie.derniereCommande ||
      compagnie.eligibilite,
  );

/**
 * Déroulé de chaque compagnie (PER-2500) : une section par entrée de
 * `compagnies[]` (référentiel de graal, même sans événement), avec ses
 * événements (ceux qui portent sa clé ; ceux sans compagnie, ou d'une
 * compagnie hors référentiel, vont à la première) et `sollicitee` (false :
 * rien ne s'est passé chez elle). Sans `compagnies[]` (graal d'avant
 * PER-2498), une seule section bâtie sur le résumé de tête, avec tous les
 * événements. Les champs d'une section sont ceux d'une timeline : les autres
 * helpers (boutons, motif d'attente, devis généré) s'y appliquent tels quels.
 */
export const sectionsParCompagnie = (timeline) => {
  if (!timeline) return [];
  const evenements = timeline.evenements || [];
  const compagnies = Array.isArray(timeline.compagnies) ? timeline.compagnies.filter((c) => c && c.compagnie) : [];
  if (!compagnies.length) {
    const slug =
      timeline.compagnie || (evenements.find((e) => e.compagnie) || {}).compagnie || COMPAGNIE_PAR_DEFAUT;
    const section = {
      compagnie: slug,
      nom: nomCompagnie(slug),
      actif: true,
      etat: timeline.etat || null,
      etatLibelle: timeline.etatLibelle || null,
      etatDetail: timeline.etatDetail || null,
      majLe: timeline.majLe || null,
      commandeEnCours: timeline.commandeEnCours || null,
      derniereCommande: timeline.derniereCommande || null,
      interrupteurs: timeline.interrupteurs || {},
      eligibilite: timeline.eligibilite || null,
      evenements,
    };
    return [{ ...section, sollicitee: estSollicitee(section, evenements) }];
  }
  const connues = new Set(compagnies.map((c) => c.compagnie));
  const premiere = compagnies[0].compagnie;
  return compagnies.map((c) => {
    const siens = evenements.filter((e) => (e.compagnie && connues.has(e.compagnie) ? e.compagnie : premiere) === c.compagnie);
    return {
      ...c,
      nom: c.nom || nomCompagnie(c.compagnie),
      actif: c.actif !== false,
      etat: c.etat || null,
      interrupteurs: c.interrupteurs || {},
      evenements: siens,
      sollicitee: estSollicitee(c, siens),
    };
  });
};

/**
 * Compagnies sollicitées sur la demande, avec leur état (PER-2500, ST1-2214) :
 * celles de la timeline de l'onglet quand elle est chargée (une section
 * sollicitée = un état, un événement, une commande ou un verdict), sinon
 * celles du résumé de la recherche de graal (`lead.agent.compagnies`, une
 * entrée par compagnie où l'agent a posé un état). Rien sans l'un ni l'autre
 * (graal d'avant ST1-2214, onglet jamais ouvert).
 */
export const compagniesSollicitees = (timeline, agent = null) => {
  if (timeline) {
    return sectionsParCompagnie(timeline)
      .filter((s) => s.sollicitee)
      .map((s) => ({ compagnie: s.compagnie, nom: s.nom, etat: s.etat || null }));
  }
  const resume = Array.isArray(agent?.compagnies) ? agent.compagnies : [];
  return resume
    .filter((c) => c && c.compagnie && c.etat)
    .map((c) => ({ compagnie: c.compagnie, nom: c.nom || nomCompagnie(c.compagnie), etat: c.etat }));
};

/**
 * Résumé pour la pastille de l'onglet « Agent » (PER-2500, ST1-2214) : les
 * compagnies sollicitées (`compagnies`, nom et état), combien, et si leurs
 * états diffèrent. `parCompagnie` : plusieurs compagnies dans des états
 * différents ; la carte montre alors une pastille par compagnie plutôt que
 * le seul résumé de graal (`lead.agent`, l'état le plus urgent), qui
 * cacherait un devis envoyé chez l'une derrière l'échec de l'autre. `libelle`
 * (« 2 compagnies ») n'est posé que dans ce cas ; `detail` nomme chaque
 * compagnie avec son état, pour l'infobulle et les lecteurs d'écran.
 */
export const resumeCompagnies = (timeline, agent = null) => {
  const compagnies = compagniesSollicitees(timeline, agent);
  const etats = new Set(compagnies.map((c) => c.etat || null));
  const etatsDifferents = etats.size > 1;
  const parCompagnie = compagnies.length > 1 && etatsDifferents;
  return {
    nb: compagnies.length,
    etatsDifferents,
    parCompagnie,
    compagnies,
    libelle: parCompagnie ? `${compagnies.length} compagnies` : null,
    detail: compagnies.map((c) => `${c.nom} : ${etatAgentInfo(c.etat).label}`).join(" · "),
  };
};

export const formatHeure = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Détail court d'un événement, lisible : formules, interrupteur, numéro… */
export const resumeDetail = (evenement) => {
  const d = evenement?.detail || {};
  if (evenement.type === "tarifs" && Array.isArray(d.formules) && d.formules.length) {
    return d.formules.map((f) => `${f.libelle} : ${f.primeMensuelle}`).join(" · ");
  }
  if (evenement.type === "depot" && d.documentUuid) {
    return d.notifie ? "Client notifié (pièces annexes seules)." : "Sans notification au client.";
  }
  if (evenement.type === "jalon" && typeof d.url === "string") return d.url;
  if (evenement.type === "fin" && d.numeroDevis) return `Devis n° ${d.numeroDevis}`;
  return "";
};

// ---------------------------------------------------------------------------
// Refonte de l'onglet (PER-2501, 28/10/2026) : une compagnie à la fois, la
// décision en tête, les événements du plus récent au plus ancien regroupés
// par lancement. Tout est dérivé de ce que graal rend, rien n'est deviné.
// ---------------------------------------------------------------------------

/** Libellés courts des états, pour les pastilles du sélecteur de compagnie. */
const LIBELLES_COURTS = {
  eligible: "Éligible",
  non_eligible: "Non éligible",
  en_file: "En file",
  en_cours: "En cours",
  attente_action: "Action attendue",
  termine: "Terminé",
  echec: "Échec",
};
export const libelleCourtEtat = (etat) => LIBELLES_COURTS[etat] || "";

// Urgence des états, celle du résumé de graal (PER-2498) : ce qui réclame
// l'équipe d'abord, puis ce qui est fini, puis les verdicts.
const ORDRE_URGENCE = ["en_cours", "en_file", "attente_action", "echec", "termine", "eligible", "non_eligible"];

const horodatage = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
};

const dateValide = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Heure d'un événement avec les secondes (« 15:37:04 ») : la date est portée par l'en-tête du lancement. */
export const formatHeureSeconde = (iso) => {
  const d = dateValide(iso);
  return d ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
};

const formatJourHeure = (iso) => {
  const d = dateValide(iso);
  if (!d) return "";
  const jour = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  const heure = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${jour} à ${heure}`;
};

/** Durée lisible d'un lancement : « 47 s », « 1 min 17 s », « 2 h 03 min ». */
export const formatDuree = (ms) => {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (m) return `${m} min ${String(s).padStart(2, "0")} s`;
  return `${s} s`;
};

/** Les événements du plus récent au plus ancien ; à la même seconde, l'ordre chronologique de graal est inversé. */
export const plusRecentDabord = (evenements) =>
  (evenements || [])
    .map((evenement, rang) => ({ evenement, rang }))
    .sort((a, b) => horodatage(b.evenement.survenuLe) - horodatage(a.evenement.survenuLe) || b.rang - a.rang)
    .map(({ evenement }) => evenement);

/**
 * Lancements d'une compagnie : un groupe par job de l'agent (`jobId`) ; un
 * événement sans job (verdict d'éligibilité) forme son propre groupe. Les
 * groupes vont du plus récent au plus ancien, leurs événements aussi. L'état
 * d'un groupe est celui que l'agent déclare après son dernier événement.
 */
export const lancements = (evenements) => {
  const groupes = new Map();
  (evenements || []).forEach((e, rang) => {
    const cle = e.jobId ? `job:${e.jobId}` : `seul:${e.uuid || rang}`;
    if (!groupes.has(cle)) groupes.set(cle, { id: cle, jobId: e.jobId || null, evenements: [] });
    groupes.get(cle).evenements.push(e);
  });
  return [...groupes.values()]
    .map((groupe) => {
      const recents = plusRecentDabord(groupe.evenements);
      const dernier = recents[0];
      const premier = recents[recents.length - 1];
      let type = "evenement";
      if (groupe.jobId) type = "lancement";
      else if (dernier.type === "eligibilite") type = "verdict";
      return {
        ...groupe,
        type,
        evenements: recents,
        debutLe: premier.survenuLe || null,
        finLe: dernier.survenuLe || null,
        dureeMs: Math.max(0, horodatage(dernier.survenuLe) - horodatage(premier.survenuLe)),
        etat: dernier.etat || null,
        etatDetail: dernier.etatDetail || null,
        enCours: ["en_cours", "en_file"].includes(dernier.etat),
      };
    })
    .sort((a, b) => horodatage(b.finLe) - horodatage(a.finLe));
};

/** Titre d'un groupe : « Lancement du 27/10 à 15:35 », « Verdict du 27/10 à 11:18 », ou le libellé d'un événement isolé. */
export const titreLancement = (groupe) => {
  if (!groupe) return "";
  if (groupe.type === "lancement") return `Lancement du ${formatJourHeure(groupe.debutLe)}`;
  if (groupe.type === "verdict") return `Verdict du ${formatJourHeure(groupe.debutLe)}`;
  return groupe.evenements?.[0]?.libelle || "";
};

/**
 * La compagnie à afficher d'abord : celle du résumé de graal (l'état le plus
 * urgent, PER-2498) quand elle est dans les sections ; sinon la plus urgente
 * selon le même ordre, à égalité la plus récemment mise à jour.
 */
export const compagnieParDefaut = (sections, compagnieResume) => {
  if (!Array.isArray(sections) || !sections.length) return null;
  if (compagnieResume && sections.some((s) => s.compagnie === compagnieResume)) return compagnieResume;
  const rang = (s) => {
    const i = ORDRE_URGENCE.indexOf(s.etat);
    return i === -1 ? ORDRE_URGENCE.length : i;
  };
  return [...sections].sort((a, b) => rang(a) - rang(b) || horodatage(b.majLe) - horodatage(a.majLe))[0].compagnie;
};

/**
 * Ce que la carte de décision dit de l'arrêt : le dernier événement de la
 * compagnie, tel que l'agent l'a écrit (libellé, puis explication ou détail
 * court). Null quand l'agent n'est jamais intervenu chez elle.
 */
export const resumeArret = (section) => {
  const dernier = plusRecentDabord(section?.evenements)[0];
  if (!dernier) return null;
  return {
    libelle: dernier.libelle || "",
    texte: dernier.explication || resumeDetail(dernier),
    niveau: dernier.niveau || "ok",
    type: dernier.type || "info",
    survenuLe: dernier.survenuLe || null,
  };
};
